import React, { FC, ChangeEvent, useEffect, useState } from 'react';
import { getDatePickerFormat, getDateFnsLocale } from 'utils/format';
import { Box, Button, MenuItem, Select, SelectChangeEvent, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Steps } from 'models/workflow-step';
import { User } from 'models/user';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { millingDeadlineDate } from 'utils/milling-deadline-date';
import { useQuery } from '@apollo/client';
import { getTechniciansForUserListTemplate } from 'apollo/templates/user/query.graphql';
import { labSettingsTemplate } from 'apollo/templates/organization/query.graphql';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { Organization } from 'models/organization';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type SendToStepDialogProps = {
    currentStepName?: string;
    currentStepUserId?: string;
    step: Steps | null;
    onConfirm: (userId: string, comment: string, deadline: Date | null) => void;
    onClose: () => void;
};

const SendToStepDialog: FC<SendToStepDialogProps> = ({
    step,
    currentStepName,
    currentStepUserId,
    onConfirm,
    onClose,
}) => {
    const locale = localStorage.getItem('locale') || 'sv-SE';
    const { t: translate } = useTranslation(['common', 'workflow', 'milling-jobs']);
    const currentUser = currentUserVar();
    const currentOrg = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();

    const isLabMillingEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.LAB_MILLING,
    );

    // DD-3391: no external tier may read the lab's user list, so asking for it is a
    // hard ACCESS_DENIED. They are the technician on their own work anyway, so the
    // picker is seeded with them rather than left showing a raw id.
    const isExternalUser = permissions.isExternalTechnician(currentUser.role);
    const [users, setUsers] = useState<User[]>(isExternalUser ? [currentUser] : []);
    const [labAvailability, setLabAvailability] = useState<Organization>();
    const [userId, setUserId] = useState(currentUser._id);
    const [comment, setComment] = useState('');
    const [deadline, setDeadline] = useState<Date | null>(null);

    useQuery(getTechniciansForUserListTemplate, {
        skip: isExternalUser,
        variables: {
            labId: (currentUser.organization as Organization)?._id,
            filters: { active: true },
            limit: 0,
            offset: 0,
        },
        onCompleted: ({ technicians }) => setUsers(technicians),
        onError: (error) => showBoundary(error),
    });

    useQuery(labSettingsTemplate, {
        onCompleted: ({ currentLab }) => setLabAvailability({ ...currentLab }),
        onError: (error) => showBoundary(error),
    });

    useEffect(() => {
        if (step) {
            setUserId(currentStepUserId || currentUser._id);
            setComment('');
        }
    }, [step]);

    useEffect(() => {
        if (isLabMillingEnabled && step?.useMillingDeadline && step?.millingDeadlineDays && labAvailability) {
            setDeadline(millingDeadlineDate(step.millingDeadlineDays as number, labAvailability));
        } else {
            setDeadline(null);
        }
    }, [step, labAvailability]);

    const displayCurrentStepName =
        currentStepName === 'ALL_STEPS_DONE'
            ? translate('workflow:ALL_STEPS_DONE')
            : currentStepName === 'NO_STEP'
              ? translate('workflow:NO_STEP')
              : currentStepName;

    const stepLabel = step?.name ? step.name : translate('workflow:NO_STEP');
    const dialogTitle = `${translate('workflow:MOVE_TO')}: ${stepLabel}`;

    return (
        <DHDialog
            open={!!step}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            title={dialogTitle}
            actions={
                <>
                    <Button onClick={onClose}>{translate('common:CANCEL')}</Button>
                    <Button
                        variant="contained"
                        disabled={!userId}
                        onClick={() => onConfirm(userId, comment, deadline)}
                        data-test="work-flow-actions.confirm-step-btn"
                    >
                        {translate('orders:CHANGE_STEP')}
                    </Button>
                </>
            }
        >
            <Box>
                <Box mb={2}>
                    <Typography variant="subtitle2">
                        {translate('workflow:WHO_COMPLETED_STEP', {
                            stepName: displayCurrentStepName,
                            interpolation: { escapeValue: false },
                        })}
                    </Typography>
                    <Select
                        fullWidth
                        value={userId}
                        onChange={(e: SelectChangeEvent) => setUserId(e.target.value)}
                        renderValue={(value) => {
                            const item = users.find(({ _id: v }) => v === value);
                            return item ? `${item.name.firstName} ${item.name.lastName}` : value;
                        }}
                        data-test="work-flow-actions.user-select"
                    >
                        {users.map((user, index) => (
                            <MenuItem value={user._id} key={index}>
                                {`${user.name.firstName} ${user.name.lastName}`}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box mb={2}>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={comment}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setComment(e.target.value)}
                        label={translate('workflow:STEP_COMMENTS')}
                        data-test="work-flow-actions.comment-txt"
                    />
                </Box>
                {isLabMillingEnabled && step?.useMillingDeadline ? (
                    <Box mb={2}>
                        <Typography variant="subtitle2">{translate('milling-jobs:MILLING_DEADLINE')}</Typography>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                            <DatePicker
                                format={getDatePickerFormat()}
                                minDate={new Date()}
                                value={deadline}
                                onChange={(date) => setDeadline(date)}
                                data-test="work-flow-actions.deadline-picker"
                            />
                        </LocalizationProvider>
                    </Box>
                ) : undefined}
            </Box>
        </DHDialog>
    );
};

export default SendToStepDialog;
